import { reportApiFailure } from "../../utils/apiTrace";
import {
  base64ToBytes,
  canDecompress,
  createSession,
  randomNonceString,
} from "../../utils/tunnel/crypto";
import { routeCode } from "../../utils/tunnel/routeCode";
import { getPublicServerEndpoint } from "../helper";

/**
 * `fetch`, but calls to our API go through the encrypted tunnel (POST /t).
 * See backend/docs/encryption-plan.md and backend/docs/tunnel-debugging.md.
 *
 * Same signature and same result as `fetch(url, init)`: the caller gets a real
 * `Response` with the logical status, headers and JSON body, so migrating a
 * call site is `fetch(` → `tunnelFetch(` and nothing else.
 *
 * Anything that is not a browser call to getPublicServerEndpoint() — SSR,
 * the Next /api routes, S3, third parties — is passed to `fetch` untouched.
 *
 * ── Falls back to plaintext, never fails closed ─────────────────────────────
 *
 * The backend accepts plaintext and tunnel side by side until the plan's
 * phase 6. So if the tunnel is off in this build, WebCrypto is missing (an
 * http:// LAN address is not a secure context), the backend has no /t (404),
 * or it does not know our key (401) or could not open the frame (400), the
 * request is sent as a normal fetch instead. None of those outer statuses mean
 * the logical request ran, so the retry cannot double a mutation.
 *
 * Deliberately NOT done by patching window.fetch (utils/apiTrace.js does that,
 * and promises to leave every request's semantics alone).
 */

const ENABLED = process.env.NEXT_PUBLIC_TUNNEL === "1";
const KEY = process.env.NEXT_PUBLIC_TUNNEL_KEY || "";
// Build-time only. Never set this for a production build: it logs decrypted traffic.
const DEBUG = process.env.NEXT_PUBLIC_TUNNEL_DEBUG === "1";
const BYPASS_STORAGE_KEY = "tunnel:bypass";
// The gateway's own refusals: in every one of them the logical request never ran.
const FALLBACK_STATUSES = new Set([400, 401, 404, 413]);
// Of those, the ones that will fail the same way for every call on this page.
const DISABLE_STATUSES = new Set([400, 401, 404]);
const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);
// Outer headers worth handing to the caller: apiTrace reads x-trace-id off the response.
const OUTER_PASSTHROUGH = ["x-request-id", "x-trace-id"];

let sessionPromise = null;
let serverUnavailable = false;
let clockOffsetMs = 0;
// Rejections that came from the network (offline, CORS, abort), as opposed to our own code.
const networkErrors = new WeakSet();
// Failures after the server answered 200, i.e. after the logical request already ran.
const afterSendErrors = new WeakSet();

let parsedKey;
/** The embedded server key, or null. A malformed value disables the tunnel; it must not throw. */
const parseKey = () => {
  if (parsedKey === undefined) {
    parsedKey = null;
    try {
      const [kid, publicKey] = KEY.split(":");
      if (kid && publicKey && /^\d+$/.test(kid) && Number(kid) <= 255) {
        parsedKey = { kid: Number(kid), publicKey: base64ToBytes(publicKey) };
      }
    } catch {
      // Not base64. Leave it null: every call goes plaintext.
    }
  }
  return parsedKey;
};

const getSession = () => {
  if (!sessionPromise) {
    sessionPromise = createSession(parseKey()).catch((err) => {
      sessionPromise = null;
      throw err;
    });
  }
  return sessionPromise;
};

const debugBypassed = () => {
  if (!DEBUG) return false;
  try {
    return window.localStorage.getItem(BYPASS_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const apiBase = () => new URL(getPublicServerEndpoint());

/** The logical request, or null when this call is not ours to tunnel. */
const toLogicalRequest = (input, init) => {
  if (!ENABLED || serverUnavailable || typeof window === "undefined") return null;
  if (!window.crypto || !window.crypto.subtle || !parseKey() || debugBypassed()) return null;
  if (typeof Request !== "undefined" && input instanceof Request) return null;

  const base = apiBase();
  const url = new URL(String(input), window.location.href);
  const basePath = base.pathname.replace(/\/$/, "");
  if (url.origin !== base.origin || !url.pathname.startsWith(`${basePath}/`)) return null;

  let body = null;
  if (init.body != null) {
    if (typeof init.body !== "string") return null; // FormData, Blob: not tunnelable
    try {
      body = JSON.parse(init.body);
    } catch {
      return null;
    }
  }

  const q = {};
  url.searchParams.forEach((value, key) => {
    q[key] = key in q ? [].concat(q[key], value) : value;
  });

  const h = { "x-platform": "web" };
  new Headers(init.headers || undefined).forEach((value, name) => {
    h[name] = value;
  });

  return {
    base,
    envelope: {
      m: String(init.method || "GET").toUpperCase(),
      p: url.pathname.slice(basePath.length),
      q,
      h,
      b: body,
    },
  };
};

const toResponse = (inner, outer) => {
  const headers = new Headers();
  Object.entries(inner.h || {}).forEach(([name, value]) => headers.set(name, String(value)));
  OUTER_PASSTHROUGH.forEach((name) => {
    const value = outer.headers.get(name);
    if (value) headers.set(name, value);
  });
  let body = null;
  if (!NULL_BODY_STATUSES.has(inner.s) && inner.b != null) {
    body = typeof inner.b === "string" ? inner.b : JSON.stringify(inner.b);
  }
  return new Response(body, { status: inner.s, headers });
};

const send = async (logical, init) => {
  const session = await getSession();
  const envelope = {
    ...logical.envelope,
    ts: Date.now() + clockOffsetMs,
    n: randomNonceString(),
    z: canDecompress(),
  };
  const { body, nonce } = await session.sealRequest(envelope);
  const basePath = logical.base.pathname.replace(/\/$/, "");
  // GETs carry no Content-Type: a POST with a bare binary body is a CORS "simple" request, so a
  // catalogue read costs the buyer no preflight round trip.
  //
  // Mutations do. Before the tunnel their JSON Content-Type made them preflighted anyway, which is
  // why utils/apiTrace.js attached `traceparent` and the PostHog session to them — the
  // PostHog → Tempo join for checkout. Declaring the binary type keeps them non-simple, so
  // apiTrace still sees "already preflighted" and adds those headers to this outer request, where
  // the backend's tracing reads them. Same round trips as before, same correlation.
  const headers =
    logical.envelope.m === "GET" ? undefined : { "Content-Type": "application/octet-stream" };
  const outer = await fetch(`${logical.base.origin}${basePath}/t`, {
    method: "POST",
    headers,
    body,
    signal: init.signal,
    credentials: init.credentials,
    keepalive: init.keepalive,
  }).catch((err) => {
    if (err && typeof err === "object") networkErrors.add(err);
    throw err;
  });
  if (outer.status !== 200) return { outer };
  // An abort or a connection dropped mid-body fails here, and is the network's, not ours.
  const sealed = await outer.arrayBuffer().catch((err) => {
    if (err && typeof err === "object") networkErrors.add(err);
    throw err;
  });
  let inner;
  try {
    inner = await session.openResponse(sealed, nonce);
  } catch (err) {
    if (err && typeof err === "object") afterSendErrors.add(err);
    throw err;
  }
  return { outer, inner, envelope, sentBytes: body.length };
};

// ── Debugging (NEXT_PUBLIC_TUNNEL_DEBUG=1 builds only) ─────────────────────

const debugLog = [];

const logExchange = ({ envelope, inner, outer, sentBytes, ms, via }) => {
  if (!DEBUG) return;
  const status = inner ? inner.s : outer && outer.status;
  const entry = {
    via,
    method: envelope.m,
    path: envelope.p,
    query: envelope.q,
    status,
    ms,
    requestBody: envelope.b,
    responseBody: inner ? inner.b : undefined,
    responseHeaders: inner ? inner.h : undefined,
    sentBytes,
    requestId: outer && outer.headers.get("x-request-id"),
  };
  debugLog.push(entry);
  if (debugLog.length > 200) debugLog.shift();
  console.groupCollapsed(
    `%c[tunnel]%c ${entry.method} ${entry.path} → ${status} %c(${via}, ${ms} ms)`,
    "color:#7c3aed;font-weight:bold",
    "",
    "color:#888",
  );
  console.log("request", { query: entry.query, headers: envelope.h, body: entry.requestBody });
  console.log("response", { headers: entry.responseHeaders, body: entry.responseBody });
  console.log("x-request-id", entry.requestId);
  console.groupEnd();
};

if (DEBUG && typeof window !== "undefined") {
  window.__tunnel = {
    get enabled() {
      return ENABLED && !serverUnavailable && !debugBypassed();
    },
    /** Every exchange since load, newest last. `console.table(__tunnel.log)` reads well. */
    log: debugLog,
    /** Send plaintext from the next reload on, to compare against the tunnel in the Network tab. */
    bypass() {
      window.localStorage.setItem(BYPASS_STORAGE_KEY, "1");
      return "Tunnel bypassed after reload. __tunnel.restore() to undo.";
    },
    restore() {
      window.localStorage.removeItem(BYPASS_STORAGE_KEY);
      return "Tunnel restored after reload.";
    },
  };
}

// ── Failure reporting ──────────────────────────────────────────────────────

/**
 * API_REQUEST_FAILED for a tunnelled call. utils/apiTrace.js leaves /t alone: it would only ever
 * see `POST /t`, and a server fault inside the tunnel reaches it as an outer 200.
 *
 * The route goes to PostHog as `t:<code>`, never as the path — the event payload is readable in
 * DevTools. The backend logs the same code as `route_code` on the request, so a code is one Loki
 * query from its route; `node src/scripts/tunnel-route-code.js <path>` goes the other way.
 * Same rule as apiTrace otherwise: 5xx and dropped connections only.
 */
const reportFailure = (logical, { status, outer, message }) => {
  routeCode(logical.envelope.p)
    .then((code) =>
      reportApiFailure({
        traceId: (outer && outer.headers.get("x-trace-id")) || undefined,
        status,
        method: logical.envelope.m,
        route: `t:${code}`,
        message,
      }),
    )
    .catch(() => {
      // Reporting a failure must never raise one.
    });
};

// ── Entry point ────────────────────────────────────────────────────────────

export async function tunnelFetch(input, init = {}) {
  const logical = toLogicalRequest(input, init);
  if (!logical) return fetch(input, init);

  const started = Date.now();
  let result;
  try {
    result = await send(logical, init);
    if (result.inner && result.inner.s === 409 && result.inner.b?.code === "CLOCK_SKEW") {
      // This device's clock is off by more than the server's window. Correct and retry once.
      clockOffsetMs = result.inner.b.serverTime - Date.now();
      result = await send(logical, init);
    }
  } catch (err) {
    // An abort or a dropped connection is the caller's to handle, exactly as with fetch.
    if (err && typeof err === "object" && networkErrors.has(err)) {
      reportFailure(logical, { status: undefined, message: err.message });
      throw err;
    }
    // Anything else is a WebCrypto/decrypt/decode failure on our side: use plaintext for this page.
    serverUnavailable = true;
    // But a mutation whose reply could not be opened has already run on the server — placing an
    // order, say. Resending it as plaintext would run it twice, so the caller gets the error, as
    // it would for a connection dropped mid-response. A read is safe to resend.
    if (afterSendErrors.has(err) && logical.envelope.m !== "GET") {
      reportFailure(logical, { status: undefined, message: err && err.message });
      throw err;
    }
    if (DEBUG) console.warn("[tunnel] disabled for this page:", err);
    return fetch(input, init);
  }

  if (!result.inner) {
    if (FALLBACK_STATUSES.has(result.outer.status)) {
      // 404: this backend has no tunnel. 400/401: it could not open our frame (wrong or rotated
      // key). 413: too big for it. Either way the logical request never ran, so plaintext is
      // safe; and for the first three every later call would fail the same way, so stop trying.
      if (DISABLE_STATUSES.has(result.outer.status)) serverUnavailable = true;
      logExchange({
        envelope: logical.envelope,
        outer: result.outer,
        ms: Date.now() - started,
        via: `fallback ${result.outer.status}`,
      });
      return fetch(input, init);
    }
    // 429 and 5xx from the gateway itself: hand the caller the outer response as-is.
    if (result.outer.status >= 500) {
      reportFailure(logical, { status: result.outer.status, outer: result.outer });
    }
    return result.outer;
  }

  if (result.inner.s === 404 && result.inner.b?.code === "NOT_TUNNELABLE") {
    // The gateway refused this path before routing it (excluded, or characters it won't accept),
    // so the logical request never ran and plaintext is safe. Other paths still tunnel.
    // (The 500 NOT_TUNNELABLE is different: that one comes after the handler ran.)
    logExchange({ ...result, ms: Date.now() - started, via: "fallback NOT_TUNNELABLE" });
    return fetch(input, init);
  }

  if (result.inner.s >= 500) {
    reportFailure(logical, { status: result.inner.s, outer: result.outer });
  }
  logExchange({ ...result, ms: Date.now() - started, via: "tunnel" });
  return toResponse(result.inner, result.outer);
}

export default tunnelFetch;
